/**
 * Creates a function that limits the number of concurrently running async tasks.
 *
 * Tasks that exceed the limit are queued and automatically start
 * when a running task completes.
 *
 * @param concurrency - Maximum number of tasks allowed to run simultaneously.
 *   Must be a positive integer.
 * @throws {Error} If `concurrency` is not a positive integer.
 * @returns A `limit` function. Wrap any async task with it to apply the limit.
 *
 * @example
 * const limit = createConcurrencyLimiter(3)
 *
 * const files = [file1, file2, ..., file15]
 *
 * // Only 3 uploads run at the same time; the rest wait in queue.
 * await Promise.all(files.map(file => limit(() => uploadFile(file))))
 */
export function createConcurrencyLimiter(concurrency: number) {
  if (!Number.isInteger(concurrency) || concurrency < 1) {
    throw new Error(`concurrency must be a positive integer, got: ${concurrency}`)
  }

  const queue: Array<() => void> = []
  let activeCount = 0

  const next = (): void => {
    activeCount--
    if (queue.length > 0) {
      queue.shift()?.()
    }
  }

  return async <T>(fn: () => Promise<T>): Promise<T> => {
    if (activeCount >= concurrency) {
      await new Promise<void>(resolve => queue.push(resolve))
    }

    activeCount++
    try {
      return await fn()
    } finally {
      next()
    }
  }
}
